import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, FileText, CornerDownLeft } from "lucide-react";
import { searchDocs } from "@/lib/markdownUtils";

/**
 * Unified overlay used for three Vault commands:
 *  - mode "switcher": Quick Switcher (Cmd/Ctrl-O) — fuzzy jump to a note
 *  - mode "command" : Command Palette (Cmd/Ctrl-P) — run an action
 *  - mode "search"  : Global full-text search (Cmd/Ctrl-Shift-F)
 */
export default function VaultPalette({ mode, docs, commands, onClose, onOpenDoc, onRunCommand }) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setIdx(0); }, [q, mode]);

  const placeholder =
    mode === "command" ? "Type a command…" :
    mode === "search" ? "Search all notes…" :
    "Jump to a note…";

  const results = useMemo(() => {
    if (mode === "command") {
      const s = q.trim().toLowerCase();
      return (commands || [])
        .filter((c) => !s || c.label.toLowerCase().includes(s) || (c.keywords || "").includes(s))
        .map((c) => ({ kind: "command", id: c.id, label: c.label, icon: c.icon, hint: c.hint, run: () => onRunCommand(c) }));
    }
    if (mode === "search") {
      return searchDocs(docs, q).map((r) => ({ kind: "doc", id: r.doc.id, label: r.doc.name, snippet: r.snippet, folder: r.doc.folder, run: () => onOpenDoc(r.doc.id) }));
    }
    // switcher
    const s = q.trim().toLowerCase();
    return (docs || [])
      .filter((d) => !s || (d.name || "").toLowerCase().includes(s) || (d.folder || "").toLowerCase().includes(s))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .slice(0, 60)
      .map((d) => ({ kind: "doc", id: d.id, label: d.name, folder: d.folder, run: () => onOpenDoc(d.id) }));
  }, [q, mode, docs, commands, onOpenDoc, onRunCommand]);

  const choose = (i) => {
    const r = results[i];
    if (r) { r.run(); onClose(); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(results.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(idx); }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-row="${idx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  const title = mode === "command" ? "Command palette" : mode === "search" ? "Search" : "Quick switcher";

  return (
    <div className="vpal-overlay" onMouseDown={onClose} data-testid="vault-palette">
      <div className="vpal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="vpal-input">
          <Search size={16} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            data-testid="vault-palette-input"
          />
          <span className="vpal-mode">{title}</span>
        </div>
        <div className="vpal-list" ref={listRef} data-testid="vault-palette-results">
          {results.length === 0 && (
            <div className="vpal-empty">{q.trim() ? "No matches." : (mode === "search" ? "Start typing to search…" : "No items.")}</div>
          )}
          {results.map((r, i) => (
            <button
              key={r.kind + r.id + i}
              data-row={i}
              className={`vpal-row ${i === idx ? "active" : ""}`}
              onMouseEnter={() => setIdx(i)}
              onClick={() => choose(i)}
              data-testid={`vault-palette-row-${i}`}
            >
              <span className="vpal-row-icon">{r.icon ? <r.icon size={15} /> : <FileText size={15} />}</span>
              <span className="vpal-row-main">
                <span className="vpal-row-label">{r.label}</span>
                {r.snippet && <span className="vpal-row-snippet">{r.snippet}</span>}
                {!r.snippet && r.folder && <span className="vpal-row-folder">{r.folder}</span>}
                {r.hint && <span className="vpal-row-folder">{r.hint}</span>}
              </span>
              {i === idx && <CornerDownLeft size={13} className="vpal-row-enter" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
